//@ts-ignore
//@ts-nocheck
import {
  all,
  put,
  select,
  take,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects';
import {
  apiRequestFailed,
  apiRequestSuccess,
  requestDataIfNecessary,
} from 'services/api/api.actions';
import { createIsApiActionOf } from 'services/api/api.helpers';
import { logoutRequest } from './session.actions';
import { authTokenKey, verifySessionEndpoint } from './session.config';
import {
  AuthType,
  cleanUpAndEndUserSession,
  getAuthType,
  isAnAuthenticatedApiRequest,
  sessionErrorHandler,
  trigger401Redirect,
} from './session.helpers';
import {
  selectAuth,
  selectIsSessionValidated,
  selectLogoutUserApiConfig,
} from './session.selectors';

function* handleAuthTokenValidationError(
  action: ReturnType<typeof apiRequestFailed>,
) {
  const {
    payload: {
      resourceId,
      requestConfig,
      error,
      skipDefaultServerErrorHandling,
    },
  } = action;

  const isAnAuthRequest = isAnAuthenticatedApiRequest(requestConfig);
  const isAVerifySessionRequest = resourceId === verifySessionEndpoint;

  // Check if it is a 'verifySessionEndpoint' request.
  if (!isAnAuthRequest && !isAVerifySessionRequest) {
    return;
  }

  // If null means that the reducer has removed the token indicating
  // that the session has been invalidated.
  const isSessionValidated = yield select(selectIsSessionValidated);

  if (!isSessionValidated) {
    try {
      // Access to local-storage might throw error we need to ensure that
      // whatever happens we redirect to the login page.
      window.localStorage.removeItem(authTokenKey);

      // Post message intended for the apps to be notified that the JWT
      // is no longer valid
      if (getAuthType() === AuthType.Sso) {
        window.postMessage(
          {
            operation: 'login_request',
            refreshView: true,
          },
          '*',
        );
      }
    } finally {
      // Triggers a client-side redirect.
      trigger401Redirect();
    }
  } else {
    const maybeStatusCode = error?.response?.status;

    if (
      !skipDefaultServerErrorHandling &&
      typeof maybeStatusCode === 'number' &&
      (maybeStatusCode === 417 || maybeStatusCode >= 500)
    ) {
      window.history.pushState(null, '/oops');
    }
  }
}

function* handleAuthTokenValidationSuccess(
  action: ReturnType<typeof apiRequestSuccess>,
) {
  const {
    payload: { resourceId, requestConfig },
  } = action;

  // Check if it is a 'verifySessionEndpoint' request.
  const isAnAuthRequest = isAnAuthenticatedApiRequest(requestConfig);
  const isAVerifySessionRequest = resourceId === verifySessionEndpoint;

  // Check if it is a 'verifySessionEndpoint' request.
  if (!isAnAuthRequest && !isAVerifySessionRequest) {
    return;
  }

  const { authToken } = yield select(selectAuth);

  window.localStorage.setItem(authTokenKey, authToken);
}

function* handleUserRequestedLogout(action: ReturnType<typeof logoutRequest>) {
  try {
    const logoutApiConfig: ReturnType<typeof selectLogoutUserApiConfig> =
      yield select(selectLogoutUserApiConfig);

    yield put(requestDataIfNecessary(logoutApiConfig));

    yield take([
      createIsApiActionOf(apiRequestSuccess, logoutApiConfig.resourceId),
      createIsApiActionOf(apiRequestFailed, logoutApiConfig.resourceId),
    ]);
  } catch (sagaError) {
    sessionErrorHandler(sagaError);
  }

  // Cleans up and redirects.
  cleanUpAndEndUserSession();
}

export default function* rootSessionSaga() {
  yield all([
    takeEvery(apiRequestSuccess.type, handleAuthTokenValidationSuccess),
    takeEvery(apiRequestFailed.type, handleAuthTokenValidationError),
    takeLeading(logoutRequest.type, handleUserRequestedLogout),
  ]);
}
